import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import type Dimension from '@/Enums/ReportBuilder/Dimension.enum';
import type Metric from '@/Enums/ReportBuilder/Metric.enum';
import CustomReportChannel from '@/Enums/ReportBuilder/ReportChannel.enum';
import type { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import type { IBeeswaxEvent } from '@/Interfaces/Beeswax/Event.interface';
import type { IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import type { IDv360InsertionOrder } from '@/Interfaces/DV360/InsertionOrder.interface';
import type { ICustomReport } from '@/Interfaces/ReportBuilder/CustomReport.interface';
import type { IFilterFormData } from '@/Interfaces/ReportBuilder/FilterFormData.interface';
import type { IOmniplatformCustomReportCampaignFilter } from '@/Interfaces/ReportBuilder/Filters.interface';
import type { IScheduleFormData } from '@/Interfaces/ReportBuilder/ScheduleFormData.interface';
import type { IScheduledCustomReport } from '@/Interfaces/ReportBuilder/ScheduledCustomReport.interface';
import type {
  ICustomReportTemplate,
  ICustomReportTemplateListItem,
  ICustomReportTemplateOption,
} from '@/Interfaces/ReportBuilder/Template.interface';
import {
  ALL_CUSTOM_REPORT_TEMPLATES_ENDPOINT,
  CAMPAIGNS_LIST_ENDPOINT,
  CUSTOM_REPORTS_YOUTUBE_CAMPAIGNS,
  EVENTS_LIST_ENDPOINT,
  customReportTemplateShareEndpoint,
  customReportTemplatesEndpoint,
  customReportsEndpoint,
  scheduledCustomReportsEndpoint,
} from '@/constants/endpoints';
import { getGroupedCampaignOptions } from '@/helpers/campaigns';
import { getControlOptionsValues, getGroupedControlOptionsValues } from '@/helpers/filters/filterMapping';
import { mapGroupedGoogleCampaigns } from '@/helpers/google/campaigns';
import type { OmniplatformChannel } from '@/types/ReportBuilder/Omniplatform.type';
import type { PaginationState, PayloadSortState } from '@/types/commonFilters.types';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';

type GroupedCampaignFetcher = (
  advertiserIds: IAdvertiser['id'][],
  signal?: AbortSignal,
) => Promise<IGroupedControlOption[]>;

export const fetchScheduledReports = async (
  params: PaginationState & Partial<PayloadSortState>,
  signal: AbortSignal | undefined,
  channel: CustomReportChannel,
): Promise<IPaginatedApiResponse<IScheduledCustomReport[]>> => {
  const response = await axios.get(scheduledCustomReportsEndpoint(channel), {
    signal,
    params,
  });

  return response.data;
};

export const fetchInstantReports = async (
  params: PaginationState & Partial<PayloadSortState>,
  signal: AbortSignal | undefined,
  channel: CustomReportChannel,
): Promise<IPaginatedApiResponse<ICustomReport[]>> => {
  const response = await axios.get(customReportsEndpoint(channel), {
    signal,
    params,
  });

  return response.data;
};

export const fetchBeeswaxCampaigns = async (advertiserIds: IAdvertiser['id'][], signal?: AbortSignal) => {
  const response = await axios.get(CAMPAIGNS_LIST_ENDPOINT, {
    params: { advertisers: advertiserIds },
    signal,
  });

  return response.data;
};

export const fetchBeeswaxEvents = async (
  advertiserIds: IAdvertiser['id'][],
  signal?: AbortSignal,
): Promise<IBeeswaxEvent[]> => {
  const response = await axios.get(EVENTS_LIST_ENDPOINT, {
    params: { advertiserIds },
    signal,
  });

  return response.data;
};

export const getGroupedBeeswaxCampaignOptions: GroupedCampaignFetcher = async (advertiserIds, signal) => {
  const campaigns = await fetchBeeswaxCampaigns(advertiserIds, signal);

  return getGroupedCampaignOptions(campaigns, CustomReportChannel.Beeswax);
};

export const fetchYoutubeCampaigns = async (
  advertiserIds: IAdvertiser['id'][],
  signal?: AbortSignal,
): Promise<IDv360InsertionOrder[]> => {
  const response = await axios.get(CUSTOM_REPORTS_YOUTUBE_CAMPAIGNS, {
    params: {
      advertiserIds,
    },
    signal,
  });

  return response.data;
};

export const getGroupedYoutubeCampaignOptions: GroupedCampaignFetcher = async (advertiserIds, signal) => {
  const campaigns = await fetchYoutubeCampaigns(advertiserIds, signal);

  return mapGroupedGoogleCampaigns(campaigns, CustomReportChannel.Youtube);
};

export const fetchTemplates = async (
  signal: AbortSignal | undefined,
  channel: CustomReportChannel,
): Promise<ICustomReportTemplateOption[]> => {
  const response = await axios.get(customReportTemplatesEndpoint(channel), {
    signal,
  });

  return response.data;
};

export const fetchTemplate = async (
  id: ICustomReportTemplateOption['id'],
  signal: AbortSignal | undefined,
  channel: CustomReportChannel,
): Promise<ICustomReportTemplate> => {
  const response = await axios.get(`${customReportTemplatesEndpoint(channel)}/${id}`, {
    signal,
  });

  return response.data;
};

export const CAMPAIGN_OPTIONS_FETCH_MAP: Record<OmniplatformChannel, GroupedCampaignFetcher> = {
  [CustomReportChannel.Beeswax]: getGroupedBeeswaxCampaignOptions,
  [CustomReportChannel.Youtube]: getGroupedYoutubeCampaignOptions,
};

//#region mutative requests
//#region helpers
const getNamePart = (entities: IControlOption<string, any>[] | undefined, entityName: string, limit: number = 7) =>
  !entities?.length || entities?.length > limit
    ? `${entityName}: (${entities!.length})`
    : `${entityName}: ${entities.map(d => d.label)}`;

const generateReportName = (
  dimensions?: IControlOption<string, Dimension>[],
  metrics?: IControlOption<string, Metric>[],
): string => {
  const dimensionsPart = getNamePart(dimensions, 'Dimensions');
  const metricsPart = getNamePart(metrics, 'Metrics');

  return `(${moment().format(DateFormat.Iso8601)}) ${dimensionsPart}; ${metricsPart}`.trim();
};

const getDateParams = (reportFormData: IFilterFormData) => {
  const { dateRange, dateRangeFormat: filterDate } = reportFormData;
  return filterDate === FilterDate.CUSTOM_RANGE
    ? {
        filterDate,
        filterStartDate: dateRange[0]?.format(DateFormat.Iso8601),
        filterEndDate: dateRange[1]?.format(DateFormat.Iso8601),
      }
    : { filterDate };
};

const getBaselinePayload = (reportFormData: IFilterFormData, name?: string) => {
  const { timeBucket, platforms } = reportFormData;
  const requestDateRange = getDateParams(reportFormData);

  const filterClientIds = getControlOptionsValues(reportFormData.clients);
  const filterAdvertiserIds = getControlOptionsValues(reportFormData.advertisers);
  const filterPlatforms = platforms?.length ? getControlOptionsValues(platforms) : undefined;

  return {
    name: name ?? generateReportName(reportFormData.dimensions, reportFormData.metrics),
    dimensions: getControlOptionsValues(reportFormData.dimensions),
    metrics: getControlOptionsValues(reportFormData.metrics),
    timeBucket,
    filterClientIds,
    filterAdvertiserIds,
    platforms: filterPlatforms,
    ...requestDateRange,
  };
};

const getSingleChannelPostPayload = (
  reportFormData: IFilterFormData,
  name?: string,
  includeWholeFilterChain?: boolean,
) => {
  const { filterClientIds, filterAdvertiserIds, ...baselineFilters } = getBaselinePayload(reportFormData, name);
  const filterCampaignIds = getControlOptionsValues(reportFormData.campaigns);
  const filterEventIds = getControlOptionsValues(reportFormData.events ?? []);

  const controlsFilter: {
    filterClientIds?: number[];
    filterAdvertiserIds?: number[];
    filterCampaignIds?: number[];
    filterEventIds?: number[];
  } = { filterEventIds };

  if (includeWholeFilterChain) {
    controlsFilter.filterCampaignIds = filterCampaignIds ?? [];
    controlsFilter.filterAdvertiserIds = filterAdvertiserIds ?? [];
    controlsFilter.filterClientIds = filterClientIds ?? [];
  } else {
    if (filterCampaignIds?.length) {
      controlsFilter.filterCampaignIds = filterCampaignIds;
    } else if (filterAdvertiserIds?.length) {
      controlsFilter.filterAdvertiserIds = filterAdvertiserIds;
    } else if (filterClientIds?.length) {
      controlsFilter.filterClientIds = filterClientIds;
    }
  }

  return {
    ...baselineFilters,
    ...controlsFilter,
  };
};

const getOmniplatformPostPayload = (
  reportFormData: IFilterFormData,
  name?: string,
  includeWholeFilterChain?: boolean,
) => {
  const { campaigns, events } = reportFormData;
  const { filterClientIds, filterAdvertiserIds, ...baselineFilters } = getBaselinePayload(reportFormData, name);

  const filterCampaignIds = campaigns?.length
    ? getGroupedControlOptionsValues(campaigns as IGroupedControlOption[])
    : undefined;

  const filterEventIds = events?.length ? getControlOptionsValues(events) : undefined;

  const controlsFilter: {
    filterClientIds?: number[];
    filterAdvertiserIds?: number[];
    filterCampaignIds?: IOmniplatformCustomReportCampaignFilter;
    filterEventIds?: number[];
    platforms: OmniplatformChannel[];
  } = {
    platforms: getControlOptionsValues(reportFormData.platforms),
    filterEventIds,
  };

  if (includeWholeFilterChain) {
    controlsFilter.filterCampaignIds = filterCampaignIds ?? {};
    controlsFilter.filterAdvertiserIds = filterAdvertiserIds ?? [];
    controlsFilter.filterClientIds = filterClientIds ?? [];
  } else {
    if (filterCampaignIds) {
      controlsFilter.filterCampaignIds = filterCampaignIds;
    } else if (filterAdvertiserIds?.length) {
      controlsFilter.filterAdvertiserIds = filterAdvertiserIds;
    } else if (filterClientIds?.length) {
      controlsFilter.filterClientIds = filterClientIds;
    }
  }

  return {
    ...baselineFilters,
    ...controlsFilter,
  };
};

const getPostBody = (
  channel: CustomReportChannel,
  reportFormData: IFilterFormData,
  name?: string,
  includeWholeFilterChain?: boolean,
) => {
  if (channel === CustomReportChannel.Omniplatform) {
    return getOmniplatformPostPayload(reportFormData, name, includeWholeFilterChain);
  }
  return getSingleChannelPostPayload(reportFormData, name, includeWholeFilterChain);
};
//#endregion

export const scheduleReport = async (
  scheduleFormData: IScheduleFormData,
  reportFormData: IFilterFormData,
  channel: CustomReportChannel,
) => {
  const { filterEndDate, filterStartDate, ...reportParams } = getPostBody(channel, reportFormData);
  const { reportName, startDate, recipientEmails, interval, dateRangeFormat } = scheduleFormData;

  return await axios.post(scheduledCustomReportsEndpoint(channel), {
    reportParams: { ...reportParams, filterDate: dateRangeFormat },
    name: reportName,
    deliveryStartAt: startDate.toISOString(true),
    recipientEmails,
    interval,
  });
};

export const generateReport = async (reportFormData: IFilterFormData, channel: CustomReportChannel) => {
  await axios.post(customReportsEndpoint(channel), getPostBody(channel, reportFormData));
};

export const createTemplate = async (
  templateName: string,
  reportFormData: IFilterFormData,
  channel: CustomReportChannel,
) => {
  const payload = getPostBody(channel, reportFormData, templateName, true);

  const response = await axios.post(customReportTemplatesEndpoint(channel), payload);

  return response.data ?? {};
};

export const downloadReport = async (reportId: number | undefined, channel: CustomReportChannel) => {
  if (!reportId) return;
  try {
    const response = await axios.get(`${customReportsEndpoint(channel)}/${reportId}/download`);

    const downloadUrl = response.data;
    if (!downloadUrl) {
      toast.error('Download failed. The report is either empty or failed to generate.');
      return;
    }
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.click();
  } catch (error) {
    toast.error('Failed to download report. Please try again.');
    console.error('Download failed:', error);
  }
};

export const cancelScheduledReport = async (id: number | undefined, channel: CustomReportChannel) => {
  if (!id) {
    throw new Error('No id to cancel the scheduled report by');
  }
  return await axios.delete(`${scheduledCustomReportsEndpoint(channel)}/${id}`);
};

export const updateScheduledReport = async (
  id: number,
  scheduleFormData: IScheduleFormData,
  channel: CustomReportChannel,
) => {
  const { reportName, startDate, recipientEmails, interval, dateRangeFormat } = scheduleFormData;

  return await axios.patch(`${scheduledCustomReportsEndpoint(channel)}/${id}`, {
    name: reportName,
    deliveryStartAt: startDate.toISOString(true),
    recipientEmails,
    interval,
    reportParams: { filterDate: dateRangeFormat },
  });
};

export const fetchAllReportTemplates = async (): Promise<ICustomReportTemplateListItem[]> => {
  const response = await axios.get(ALL_CUSTOM_REPORT_TEMPLATES_ENDPOINT);
  return response.data;
};

export const shareReportTemplate = async (
  id: number,
  payload: { receivingUserIds: number[]; shareWithOrg: boolean },
) => {
  const response = await axios.post(customReportTemplateShareEndpoint(id), payload);
  return response.data;
};

export const deleteReportTemplate = async (id: number, channel: CustomReportChannel) => {
  return await axios.delete(`${customReportTemplatesEndpoint(channel)}/${id}`);
};
//#endregion
